<template>
  <v-dialog v-model="dialog" persistent max-width="500">
    <v-card>
      <v-card-title> Order Digital Gifts </v-card-title>
      <v-card-text>
        <div class="current-balance mb-6">
          <div>
            Current candles: {{ currentCandles }}
            <span v-if="candles > 0" class="potential-amount">
              (Will have: {{ currentCandles + candles }})
            </span>
          </div>
          <div>
            Current flowers: {{ currentFlowers }}
            <span v-if="flowers > 0" class="potential-amount">
              (Will have: {{ currentFlowers + flowers }})
            </span>
          </div>
        </div>

        <v-slider
          v-model="candles"
          min="0"
          max="30"
          dense
          thumb-size="20"
          thumb-label="always"
          class="mb-4"
        >
          <template v-slot:prepend>
            <v-icon>mdi-candle</v-icon>
          </template>
        </v-slider>

        <v-slider
          v-model="flowers"
          min="0"
          max="30"
          dense
          thumb-size="20"
          thumb-label="always"
          class="mb-4"
        >
          <template v-slot:prepend>
            <v-icon>mdi-flower</v-icon>
          </template>
        </v-slider>

        <div class="pricing-info">
          <div class="subtotal">
            <div>
              Candles ({{ candles }} x
              <span v-if="discount > 0" class="original-price">€0.50</span>
              <span v-if="discount > 0" class="discounted-price"
                >€{{ (0.5 * (1 - discount / 100)).toFixed(2) }}</span
              >
              <span v-else>€0.50</span>
              ):
              <span v-if="discount > 0" class="original-price"
                >€{{ (candles * 0.5).toFixed(2) }}</span
              >
              <span class="discounted-total"
                >€{{ (candles * 0.5 * (1 - discount / 100)).toFixed(2) }}</span
              >
            </div>
            <div>
              Flowers ({{ flowers }} x
              <span v-if="discount > 0" class="original-price">€0.80</span>
              <span v-if="discount > 0" class="discounted-price"
                >€{{ (0.8 * (1 - discount / 100)).toFixed(2) }}</span
              >
              <span v-else>€0.80</span>
              ):
              <span v-if="discount > 0" class="original-price"
                >€{{ (flowers * 0.8).toFixed(2) }}</span
              >
              <span class="discounted-total"
                >€{{ (flowers * 0.8 * (1 - discount / 100)).toFixed(2) }}</span
              >
            </div>
          </div>

          <div v-if="totalItems < 3" class="info-message error-text">
            Minimum order is 3 items
          </div>

          <div class="info-message" v-if="discountMessage.visible">
            {{ discountMessage.text }}
          </div>

          <div class="total font-weight-bold">
            Total:
            <span v-if="discount > 0" class="original-price"
              >€{{ (flowers * 0.8 + candles * 0.5).toFixed(2) }}</span
            >
            <span class="discounted-total">€{{ finalPrice.toFixed(2) }}</span>
            <span v-if="discount > 0" class="discount-label"
              >({{ discount }}% off)</span
            >
          </div>
        </div>

        <v-btn
          block
          class="mt-4 btn-bg"
          :disabled="totalItems < 3"
          @click="orderGifts"
          elevation="0"
        >
          Buy Now
        </v-btn>
        <v-btn block class="mt-4" 
          color="primary"
          text @click="closeDialog" elevation="0">
          Close
        </v-btn>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "OrderDigitalGifts",
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    currentFlowers: {
      type: Number,
      default: 0,
    },
    currentCandles: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      flowers: 0,
      candles: 0,
    };
  },
  computed: {
    totalItems() {
      return this.flowers + this.candles;
    },

    flowersCost() {
      return this.flowers * 0.8 * (1 - this.discount / 100);
    },

    candlesCost() {
      return this.candles * 0.5 * (1 - this.discount / 100);
    },

    subtotal() {
      return this.flowersCost + this.candlesCost;
    },

    discount() {
      // Calculate discount: 5% for every 10 items, max 30%
      const discountLevel = Math.floor(this.totalItems / 10);
      const calculatedDiscount = discountLevel * 5;
      return Math.min(calculatedDiscount, 30);
    },

    discountAmount() {
      return (this.subtotal * this.discount) / 100;
    },

    finalPrice() {
      return this.subtotal - this.discountAmount;
    },

    discountMessage() {
      if (this.totalItems >= 50 && this.totalItems < 60) {
        return {
          text: `Add ${
            60 - this.totalItems
          } more items to get maximum 30% discount!`,
          visible: true,
        };
      }
      if (this.totalItems >= 40 && this.totalItems < 50) {
        return {
          text: `Add ${50 - this.totalItems} more items to get 25% discount!`,
          visible: true,
        };
      }
      if (this.totalItems >= 30 && this.totalItems < 40) {
        return {
          text: `Add ${40 - this.totalItems} more items to get 20% discount!`,
          visible: true,
        };
      }
      if (this.totalItems >= 20 && this.totalItems < 30) {
        return {
          text: `Add ${30 - this.totalItems} more items to get 15% discount!`,
          visible: true,
        };
      }
      if (this.totalItems >= 10 && this.totalItems < 20) {
        return {
          text: `Add ${20 - this.totalItems} more items to get 10% discount!`,
          visible: true,
        };
      }
      if (this.totalItems >= 3 && this.totalItems < 10) {
        return {
          text: `Add ${10 - this.totalItems} more items to get 5% discount!`,
          visible: true,
        };
      }
      return { text: "", visible: false };
    },
  },

  methods: {
    closeDialog() {
      this.flowers = 0;
      this.candles = 0;
      this.$emit("close");
    },
    orderGifts() {
      if (this.totalItems < 3) return;

      this.$emit("order", {
        flowers: this.flowers,
        candles: this.candles,
        totalPrice: this.finalPrice,
      });

      // Reset form
      this.flowers = 0;
      this.candles = 0;
    },
  },
};
</script>

<style scoped>
.v-card-title {
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.current-balance {
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 500;
}

.pricing-info {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  margin-top: 16px;
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;
}

.subtotal {
  font-size: 0.95em;
  letter-spacing: 0.3px;
}

.total {
  margin-top: 0;
  font-size: 1.2em;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.info-message {
  margin-top: 15px;
  color: #4a90e2;
  font-size: 0.9em;
  font-weight: 500;
  letter-spacing: 0.2px;
}

.error-text {
  color: #ff5252; /* Vuetify's default error red color */
}

.potential-amount {
  color: #4a90e2;
  font-size: 0.9em;
  margin-left: 8px;
  font-weight: 500;
  font-style: italic;
}

.btn-bg {
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.original-price {
  text-decoration: line-through;
  color: #999;
  margin-right: 4px;
}

.discounted-price {
  color: #2ECC71;
  font-weight: 600;
}

.discounted-total {
  color: #2ECC71;
  font-weight: 600;
}

.discount-label {
  color: #2ECC71;
  font-size: 0.9em;
  margin-left: 8px;
}
</style>